import { useCallback, useMemo } from "react"

import { SearchDetailResult } from "interface/search"
import { KojinResult, useListKojin } from "interface/kojin"

import { Grid, useGrid } from "components/grid"

import { KojinPreviewDialog } from "features/kojin/dialog/preview"
import { useKojinPreviewDialog } from "features/kojin/dialog/preview/hooks"
import { useKojinGridColumns } from "./columns"
import { useEvaluationForm } from "features/search/context"
import { KojinResultWithEvaluationSummary, useListEvaluationResults } from "interface/search/evaluation"
import { evaluationSummary } from "interface/search/evaluation/util"

type KojinPageProps = {
	search: SearchDetailResult
} & ReturnType<typeof useGrid>
export const KojinPage = ({
	search,
	...others
}: KojinPageProps) => {
	const evaluationForm = useEvaluationForm()

	const {
		kojinList: selectedKojinList,
		kojinCode: selectedKojinCode,
		authorId: selectedAuthorId,
		setKojinList,
		openKojin,
		close: closeKojinDialog
	} = useKojinPreviewDialog()

	const { apiRef, options: { kojinLimit } } = others

	const onFetched = useCallback(
		(result: KojinResult[]) => {
			result.sort((a, b) => (
				(a.score?.rank && b.score?.rank) ? a.score.rank - b.score.rank :
				(a.isiNameKana && b.isiNameKana) ? a.isiNameKana.localeCompare(b.isiNameKana) :
				0
			))
		},
		[]
	)
	const listState = useListKojin(search.searchId, undefined, {
		updateTime: search.updateTime,
		rankLimit: kojinLimit,
		onFetched
	})
	const evaluationResultState = useListEvaluationResults(
		search.searchId,
		search.evaluationUploadTime
	)
	const onChangeEvaluation = useCallback(
		(/*kojinCode: string*/) => {
			evaluationResultState.refetch()
		},
		[evaluationResultState]
	)
	const progress = useMemo(
		() => listState.loaded && listState.total ? (listState.loaded / listState.total) * 100 : undefined,
		[listState]
	)
	const dataList = useMemo<KojinResultWithEvaluationSummary[]>(
		() => {
			if (listState.loading || evaluationResultState.loading) return []
			const kojinList = listState.data
			if (!kojinList || kojinList.length === 0) return []
			if (!evaluationForm) return kojinList
			const evaluationList = evaluationResultState.data?.list
			if (!evaluationList || evaluationList.length === 0) return kojinList
			return kojinList.map(
				kojin => {
					if (!kojin.kojinCode) return kojin
					const kojinsEvaluationList = evaluationList.filter(r => r.kojinCode === kojin.kojinCode)
					if (kojinsEvaluationList.length === 0) return kojin
					return { ...kojin, evaluation: evaluationSummary(evaluationForm, kojinsEvaluationList) }
				}
			)
		},
		[listState, evaluationResultState, evaluationForm]
	)
	const onClickLink = useCallback(
		(kojinCode: string | null) => {
			if (!apiRef.current) return
			const api = apiRef.current

			const rowDataList: KojinResult[] = []
			const rowCount = api.getRowsCount()
			for(let i = 0; i < rowCount; i++) {
				const index = api.getRowIdFromRowIndex(i) as number
				if (index >= 0 && dataList.length > index) {
					rowDataList.push(dataList[index])
				}
			}
			setKojinList(rowDataList)
			openKojin(kojinCode)
		},
		[setKojinList, openKojin, apiRef, dataList]
	)
	const { columnsDef } = useKojinGridColumns({ search, evaluationForm, onClickLink })

	const onDblClick = useCallback(
		(kojin: KojinResult) => onClickLink(kojin.kojinCode ?? null),
		[onClickLink]
	)
	return (
		<>
			<Grid
				{...others}
				saveKey="kojin_grid"
				baseFileName={search.title}
				sheetName="KOL"
				loading={listState.loading}
				progress={progress}
				columnsDef={columnsDef}
				dataList={dataList}
				onDblClick={onDblClick}
			/>
			{(selectedKojinCode || selectedAuthorId) ? (
				<KojinPreviewDialog
					kojinList={selectedKojinList}
					kojinCode={selectedKojinCode ?? undefined}
					authorId={selectedAuthorId ?? undefined}
					onChangeEvaluation={onChangeEvaluation}
					onClose={closeKojinDialog}
				/>
			) : <></>}
		</>
	)
}